"use client";

import clsx from "clsx";

export const cn = (...args: Parameters<typeof clsx>) => clsx(...args);

export const inputClass =
  "bg-white rounded focus:outline-none focus:border-pinkCustom border border-gray-300 px-2 py-1";

export function getLocaleUI(locale: string) {
  const dir = locale === "fa" ? "rtl" : "ltr";
  const alignClass = dir === "rtl" ? "text-right" : "text-left";
  return {
    dir: dir as "rtl" | "ltr",
    alignClass,
    inputClass: `${inputClass} ${alignClass}`,
  };
}

export function updateAt<T>(arr: T[], index: number, updater: (item: T) => T): T[] {
  if (index < 0 || index >= arr.length) return arr;
  return arr.map((item, idx) => (idx === index ? updater(item) : item));
}

export function removeAt<T>(arr: T[], index: number): T[] {
  if (index < 0 || index >= arr.length) return arr;
  return arr.filter((_, idx) => idx !== index);
}

export function insertAtEnd<T>(arr: T[], item: T): T[] {
  return [...arr, item];
}

export function createEmptyRichText() {
  return {
    type: "doc",
    content: [{ type: "paragraph", content: [{ type: "text", text: "" }] }],
  };
}

export function extractPlainTextFromRich(rich: any): string {
  const parts: string[] = [];
  const walk = (node: any) => {
    if (!node) return;
    if (Array.isArray(node)) {
      node.forEach(walk);
      return;
    }
    if (typeof node === "object") {
      if (typeof node.text === "string") {
        parts.push(node.text);
      }
      if (Array.isArray(node.content)) {
        node.content.forEach(walk);
      }
    }
  };
  walk(rich);
  return parts.join(" ").replace(/\s+/g, " ").trim();
}

export function getFileNameFromPath(path?: string | null) {
  if (!path) return undefined;
  try {
    const parts = path.split(/[\\/]/).filter(Boolean);
    const last = parts.pop();
    if (!last) return undefined;
    return decodeURIComponent(last);
  } catch {
    return path;
  }
}

// JSON-based clone helper; only safe for plain data structures.
export function deepCloneJson<T>(value: T): T {
  const globalClone = (globalThis as any)?.structuredClone;
  if (typeof globalClone === "function") {
    try {
      return globalClone(value);
    } catch {
      // ignore structuredClone failures
    }
  }
  try {
    return JSON.parse(JSON.stringify(value));
  } catch {
    return value;
  }
}

export function toDateInputValue(value?: string | null) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toISOString().slice(0, 10);
}

export function formatDisplayDate(value?: string | null, locale: "en" | "fa" = "en") {
  if (!value) return "Not set";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Invalid date";
  try {
    const formatter = new Intl.DateTimeFormat(
      locale === "fa" ? "fa-IR-u-ca-persian" : "en-US",
      {
        dateStyle: "medium",
        calendar: locale === "fa" ? "persian" : "gregory",
      },
    );
    return formatter.format(date);
  } catch {
    return date.toLocaleDateString(locale === "fa" ? "fa-IR" : "en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  }
}

export function pickFirstNonEmpty(
  ...values: Array<string | null | undefined>
): string | undefined {
  for (const value of values) {
    if (typeof value === "string") {
      const trimmed = value.trim();
      if (trimmed) return trimmed;
    }
  }
  return undefined;
}

export function uid() {
  return typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : Math.random().toString(36).slice(2);
}

export const PRODUCER_FILTER_UNKNOWN = "__unknown";

export function getProducerKey(value?: string | null) {
  const trimmed = value?.trim();
  return trimmed && trimmed.length > 0 ? trimmed : PRODUCER_FILTER_UNKNOWN;
}

export function getProducerLabel(value?: string | null) {
  const trimmed = value?.trim();
  return trimmed && trimmed.length > 0 ? trimmed : "Unknown producer";
}

export function getBlockLocaleStrings(locale: "en" | "fa") {
  const isFa = locale === "fa";
  const blocks: Record<string, string> = {
    contact: isFa ? "تماس" : "Contact",
    heading: isFa ? "سرتیتر" : "Heading",
    paragraph: isFa ? "پاراگراف" : "Paragraph",
    image: isFa ? "تصویر" : "Image",
    divider: isFa ? "جداکننده" : "Divider",
    list: isFa ? "فهرست" : "List",
    faq: isFa ? "سوالات متداول" : "FAQ",
    quote: isFa ? "نقل قول" : "Quote",
    embed: isFa ? "ویدیو" : "Embed",
    reference: isFa ? "مرجع" : "Reference",
    contentReference: isFa ? "ارجاع محتوا" : "Content Ref",
    productCard: isFa ? "کارت محصول" : "Product Card",
    table: isFa ? "جدول" : "Table",
  };

  return {
    blocks,
    placeholders: {
      heading: isFa ? "متن سرتیتر…" : "Heading text…",
      paragraph: isFa ? "متن پاراگراف..." : "Write paragraph...",
      imageAlt: isFa ? "توضیحات تصویر…" : "Describe the image…",
      quote: isFa ? "متن نقل قول…" : "Quote…",
      cite: isFa ? "منبع…" : "Cite…",
      linkLabel: isFa ? "برچسب لینک" : "Link label",
      linkHref: "https://…",
      embedUrl: isFa ? "لینک ویدیو…" : "https://www.youtube.com/embed/…",
      listItem: (index: number) =>
        isFa ? `آیتم ${index + 1}` : `Item ${index + 1}`,
    },
    helpers: {
      selectImage: isFa ? "انتخاب تصویر" : "Select an image",
      changeImage: isFa ? "تغییر" : "Change",
      clearImage: isFa ? "حذف" : "Clear",
      heroToggle: isFa ? "علامت به عنوان تصویر هدر" : "Mark as hero image",
      shortDesc: isFa ? "استفاده به عنوان خلاصه" : "Use as short description",
      dividerSpace: isFa ? "فقط فاصله" : "Whitespace only",
      inlineLabel: isFa ? "هم‌ردیف با بلاک بعدی" : "Inline next block",
      inlineLeft: isFa ? "چپ" : "Left",
      inlineRight: isFa ? "راست" : "Right",
      noItems: isFa ? "آیتمی موجود نیست." : "No items yet.",
      ordered: isFa ? "عددی" : "Numbered",
      addItem: isFa ? "افزودن" : "Add",
      faqsLabel: isFa ? "سوالات متداول" : "FAQs",
      addFaq: isFa ? "افزودن" : "Add",
      faqEmpty: isFa ? "سوالی ثبت نشده است." : "No FAQ entries yet.",
      optionalImage: isFa ? "تصویر اختیاری" : "Optional image",
      imagePosition: isFa ? "موقعیت تصویر" : "Image position",
      imagePosInlineLeft: isFa ? "چپ (هم‌ردیف)" : "Inline left",
      imagePosInlineRight: isFa ? "راست (هم‌ردیف)" : "Inline right",
      imagePosTop: isFa ? "بالای متن" : "Above text",
      imagePosBottom: isFa ? "پایین متن" : "Below text",
      clearImg: isFa ? "حذف تصویر" : "Clear image",
      question: isFa ? "سوال" : "Question",
      answer: isFa ? "پاسخ" : "Answer",
      answerHidden: isFa ? "پاسخ مخفی است" : "Answer hidden",
      dragToReorder: isFa ? "برای جابه‌جایی بکشید" : "Drag to reorder",
      selectedImage: isFa ? "تصویر انتخاب شده" : "Selected image",
      change: isFa ? "تغییر" : "Change",
      addLabel: isFa ? "افزودن" : "Add",
      expand: isFa ? "باز کردن" : "Expand",
      collapse: isFa ? "بستن" : "Collapse",
    },
  };
}


